import { Eye, EyeOff } from 'lucide-react';
import { useSensitiveData } from '@/hooks/use-sensitive-data';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';

interface SensitiveDataProps {
  children: React.ReactNode;
  className?: string;
}

export function SensitiveData({ children, className }: SensitiveDataProps) {
  const { visible } = useSensitiveData();

  return (
    <span className={cn(className, !visible && 'blur-sm select-none bg-muted/30 rounded px-1')}>
      {children}
    </span>
  );
}

interface SensitiveDataToggleProps {
  className?: string;
}

export function SensitiveDataToggle({ className }: SensitiveDataToggleProps) {
  const { visible, setVisible } = useSensitiveData();

  // Invert the logic: switch ON = hide data (visible=false), switch OFF = show data (visible=true)
  const isHidden = !visible;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn('flex items-center gap-2', className)}>
            {visible ? (
              <Eye className="h-4 w-4 text-muted-foreground" />
            ) : (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            )}
            <Switch
              id="financial-data-toggle"
              checked={isHidden}
              onCheckedChange={(checked) => setVisible(!checked)}
            />
            <span className="text-sm text-muted-foreground">
              {isHidden ? 'Hidden' : 'Visible'}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{isHidden ? 'Reveal Financial Data' : 'Hide Financial Data'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface FloatingSensitiveDataToggleProps {
  topClassName?: string;
  className?: string;
}

export function FloatingSensitiveDataToggle({
  topClassName = 'top-32',
  className,
}: FloatingSensitiveDataToggleProps) {
  const { preferences } = useUserPreferences();
  const isRightSidebar = preferences.sidebarPosition === 'right';

  return (
    <div
      className={cn(
        'fixed z-40',
        topClassName,
        isRightSidebar ? 'left-6' : 'right-6',
        className,
      )}
    >
      <SensitiveDataToggle />
    </div>
  );
}
